import type { Ref } from 'vue';
import { useInfiniteQuery, keepPreviousData } from '@tanstack/vue-query';
import { unref } from 'vue';
import { getApi } from './api';
import { toApiPagination } from './paginated-list-fetch';
import type { PaginatedResponse } from './types/paginated';

export const ACCOUNT_DEVICE_LIST_PER_PAGE = 100;

export interface GetAccountDeviceListParams {
    with_current_position?: boolean;
    subscription_status?: string;
    exclude_groups?: boolean;
    device_status?: string;
    page?: number;
    per_page?: number;
    query?: string;
}

const DEFAULT_DEVICE_STATUS = 'active';

function normalizeParams(
    params: GetAccountDeviceListParams | undefined,
): GetAccountDeviceListParams {
    const p = params ?? {};
    return {
        exclude_groups: true,
        //device_status: p.device_status ?? DEFAULT_DEVICE_STATUS,
        ...p,
    };
}

export type AccountDeviceListPage = PaginatedResponse<Record<string, unknown>>;

export async function fetchAccountDeviceListPage(
    accountId: number,
    args: { page: number; perPage: number; query: string },
    params?: GetAccountDeviceListParams,
): Promise<AccountDeviceListPage> {
    const { api, baseURL } = getApi();
    const baseParams = normalizeParams(params);
    const pagination = toApiPagination(args);
    const requestParams: Record<string, unknown> = {
        ...baseParams,
        ...pagination,
    };
    const res = await api.get(`/api/accounts/${accountId}/device-list`, {
        baseURL,
        params: requestParams,
    });
    return res.data as AccountDeviceListPage;
}

export default (
    accountId: Ref<number> | number,
    params?: Ref<GetAccountDeviceListParams> | GetAccountDeviceListParams,
    searchQuery?: Ref<string> | string,
) =>
    useInfiniteQuery({
        queryKey: [
            'account-device-list',
            () => unref(accountId),
            () => normalizeParams(unref(params)),
            () => unref(searchQuery) ?? '',
        ],
        placeholderData: keepPreviousData,
        initialPageParam: 1,
        enabled: !!unref(accountId),
        queryFn: ({ pageParam }) =>
            fetchAccountDeviceListPage(
                unref(accountId),
                {
                    page: pageParam as number,
                    perPage:
                        normalizeParams(unref(params)).per_page ??
                        ACCOUNT_DEVICE_LIST_PER_PAGE,
                    query: unref(searchQuery) ?? '',
                },
                unref(params),
            ),
        getNextPageParam: (lastPage) => {
            const meta = lastPage?.meta;
            if (meta?.current_page != null && meta?.last_page != null) {
                return meta.current_page < meta.last_page
                    ? meta.current_page + 1
                    : undefined;
            }
            return undefined;
        },
    });
